import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink, FileText, Trash2 } from "lucide-react";
import { Tooltip } from "@mui/material";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  SepaTransactionRow,
  SepaDirectionEnum,
  SepaTransactionStatusEnum,
} from "../../../../../stores/Sepa/sepa.types";
import { PATIENT_SUBTABS } from "../../../../../stores/Patients/patientsStore";
import { PatientsContext } from "../../../../../stores/Patients/patients.provider";
import { PatientCommands } from "../../../../../stores/Patients/patient.commands";
import useStore from "../../../../../helpers/useStore";
import { useKeyboardListSelection } from "../../../../../hooks/useKeyboardListSelection";
import { SepaEmptyBlock, SepaErrorBlock, SepaLoadingBlock } from "./SepaListStates";
import { getSepaErrorMessage } from "./sepaError";

/**
 * Row type for the SEPA transaction table. `SepaTransactionResponseDto` already
 * carries `rx.date`, `document` and `user` after the client regen.
 */
export type SepaTxnTableRow = SepaTransactionRow;

const STATUS_LABEL: Record<string, string> = {
  [SepaTransactionStatusEnum.Pending]: "Offen",
  [SepaTransactionStatusEnum.Exported]: "Exportiert",
  [SepaTransactionStatusEnum.Settled]: "Abgeschlossen",
  [SepaTransactionStatusEnum.Returned]: "Zurückgegeben",
  [SepaTransactionStatusEnum.Cancelled]: "Abgebrochen",
  [SepaTransactionStatusEnum.Reversed]: "Storniert",
};

const STATUS_CLASS: Record<string, string> = {
  [SepaTransactionStatusEnum.Pending]: "border-amber-300 bg-amber-50 text-amber-700",
  [SepaTransactionStatusEnum.Exported]: "border-sky-300 bg-sky-50 text-sky-700",
  [SepaTransactionStatusEnum.Settled]: "border-emerald-300 bg-emerald-50 text-emerald-700",
  [SepaTransactionStatusEnum.Returned]: "border-rose-300 bg-rose-50 text-rose-700",
  [SepaTransactionStatusEnum.Cancelled]: "border-slate-300 bg-slate-50 text-slate-500",
  [SepaTransactionStatusEnum.Reversed]: "border-slate-300 bg-slate-50 text-slate-500",
};

/** Statuses that block reversing (storno): already reversed/cancelled, locked in a live export, or settled. */
const REVERSAL_BLOCKED: string[] = [
  SepaTransactionStatusEnum.Exported,
  SepaTransactionStatusEnum.Settled,
  SepaTransactionStatusEnum.Reversed,
  SepaTransactionStatusEnum.Cancelled,
];

const fmtEur = (value: number): string =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(value);

const fmtDate = (value?: string | Date | null): string =>
  value ? new Date(value).toLocaleDateString("de-DE") : "—";

const EMPTY_SELECTION = new Set<string>();

interface Props {
  rows: SepaTxnTableRow[];
  /** Show the leading checkbox column (PENDING/export tabs). */
  selectable?: boolean;
  /** Show the "Export" column linking a row to its export batch (the Alle tab). */
  showExportColumn?: boolean;
  /** Hide the trailing Aktion (delete) column for read-only contexts (spotlight). */
  hideActions?: boolean;
  /** Called when the export chip is clicked (e.g. to open the Exporte tab). */
  onOpenBatch?: (batch: { id: string; batchNumber: number }) => void;
  selectedIds?: Set<string>;
  onToggle?: (id: string) => void;
  onToggleAll?: () => void;
  onDownloadDocument: (documentId: string) => void;
  onReverse?: (row: SepaTxnTableRow) => void;
  /** Called when a row body is clicked (opens the export timeline). */
  onRowClick?: (row: SepaTxnTableRow) => void;
  /** Enable ↑/↓ row selection with Enter opening the row's patient (the "Alle" tab). */
  keyboardNav?: boolean;
  /** Show the free-text column (Verwendungszweck / storno reason). */
  showText?: boolean;
  busy?: boolean;
  isLoading?: boolean;
  isError?: boolean;
  /** The query error (backend message is surfaced in the error state). */
  error?: unknown;
  emptyText?: string;
  /** Drop the own border/rounding so a parent scroll container owns the frame. */
  bare?: boolean;
}

/**
 * Shared SEPA transaction table (shadcn) used by the Lastschrift / Rückerstattungen /
 * Alle tabs. Amounts are stored positive; the sign shown here is derived from
 * `direction` (a credit transfer / refund is money out → shown negative + red).
 */
const SepaTransactionTable: React.FC<Props> = ({
  rows,
  selectable,
  showExportColumn,
  hideActions,
  onOpenBatch,
  selectedIds = EMPTY_SELECTION,
  onToggle,
  onToggleAll,
  onDownloadDocument,
  onReverse,
  onRowClick,
  keyboardNav,
  showText,
  busy,
  isLoading,
  isError,
  error,
  emptyText = "Keine Transaktionen vorhanden.",
  bare,
}) => {
  const colSpan =
    10 +
    (selectable ? 1 : 0) +
    (showExportColumn ? 1 : 0) +
    (showText ? 1 : 0) -
    (hideActions ? 1 : 0);
  const allSelected = rows.length > 0 && selectedIds.size === rows.length;
  const thClass = "text-[13px] font-bold uppercase tracking-wide text-slate-600";

  const navigate = useNavigate();
  const patientsStore = useStore(PatientsContext);
  const { navigateToPatient } = PatientCommands(patientsStore);

  // The URL is a one-way projection of the (app-wide) patient store, so a plain
  // link is ignored mid-session; mutate the store instead. A real href is kept so
  // modifier-click still opens a fresh tab (a full load hydrates from the URL).
  const patientHref = (id: number) =>
    `/patient?selectedPatientId=${id}&selectedSubTabStr=${PATIENT_SUBTABS.BILLING}`;

  const goToPatient = (row: SepaTxnTableRow) => {
    if (row.patient?.id != null) {
      navigateToPatient(navigate, row.patient.id, null, PATIENT_SUBTABS.BILLING);
    }
  };

  const rowRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());
  const {
    selectedId: kbSelectedId,
    setSelectedId: setKbSelectedId,
    handleKeyDown,
  } = useKeyboardListSelection({
    items: rows,
    getId: (row) => row.id,
    onActivate: goToPatient,
  });

  // ↑/↓ move a highlighted row, Enter opens its patient. Driven off a document
  // listener (not element focus) and disabled while an input or dialog is active.
  useEffect(() => {
    if (!keyboardNav) return;
    const onKeyDown = (event: KeyboardEvent) => {
      const active = document.activeElement as HTMLElement | null;
      const tag = active?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || active?.isContentEditable) {
        return;
      }
      if (
        document.querySelector(
          '[data-state="open"][role="dialog"], [data-state="open"][role="alertdialog"]',
        )
      ) {
        return;
      }
      handleKeyDown(event as unknown as React.KeyboardEvent);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [keyboardNav, handleKeyDown]);

  useEffect(() => {
    if (keyboardNav && kbSelectedId) {
      rowRefs.current.get(kbSelectedId)?.scrollIntoView({ block: "nearest" });
    }
  }, [keyboardNav, kbSelectedId]);

  return (
    <div className={bare ? "" : "overflow-hidden rounded-lg border border-solid border-slate-200"}>
      <Table>
        <TableHeader className="sticky top-0 z-10">
          <TableRow className="border-b border-solid border-slate-200 bg-slate-100 hover:bg-slate-100">
            {selectable && (
              <TableHead className="w-10 pl-4">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={onToggleAll}
                  aria-label="Alle auswählen"
                  disabled={rows.length === 0}
                />
              </TableHead>
            )}
            <TableHead className={thClass}>Transaktion Nr.</TableHead>
            <TableHead className={thClass}>Patient</TableHead>
            <TableHead className={thClass}>Rezeptnr.</TableHead>
            <TableHead className={thClass}>Rezeptdatum</TableHead>
            <TableHead className={thClass}>Erstellt am</TableHead>
            <TableHead className={`${thClass} text-right`}>Betrag</TableHead>
            <TableHead className={`${thClass} text-center`}>Erstellt von</TableHead>
            <TableHead className={`${thClass} text-center`}>Status</TableHead>
            {showText && <TableHead className={thClass}>Verwendungszweck</TableHead>}
            {showExportColumn && <TableHead className={`${thClass} text-center`}>Export</TableHead>}
            <TableHead className={`${thClass} text-center`}>Beleg</TableHead>
            {!hideActions && <TableHead className={`${thClass} text-center`}>Aktion</TableHead>}
          </TableRow>
        </TableHeader>

        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={colSpan} className="p-0">
                <SepaLoadingBlock message="Transaktionen werden geladen…" />
              </TableCell>
            </TableRow>
          ) : isError ? (
            <TableRow>
              <TableCell colSpan={colSpan} className="p-0">
                <SepaErrorBlock
                  message={getSepaErrorMessage(
                    error,
                    "Transaktionen konnten nicht geladen werden.",
                  )}
                />
              </TableCell>
            </TableRow>
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={colSpan} className="p-0">
                <SepaEmptyBlock message={emptyText} />
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row, index) => {
              const isCredit = row.direction === SepaDirectionEnum.CreditTransfer;
              const zebra = index % 2 === 1 ? "bg-slate-100" : "bg-white";
              const selected = selectedIds.has(row.id);
              const createdBy = row.user
                ? `${row.user.firstName ?? ""} ${row.user.lastName ?? ""}`.trim()
                : "";
              const isReversal = !!row.reversalOf;
              const canReverse = !isReversal && !REVERSAL_BLOCKED.includes(row.status);
              // Refunds and reversals (storno) are money-out/corrections: show a positive value in red.
              const isMoneyOut = isCredit || isReversal;
              // A voided row (cancelled, e.g. via a revoked mandate, or a reversed original)
              // keeps its data but no longer counts: mute the whole row, strike the number.
              const isVoided =
                row.status === SepaTransactionStatusEnum.Cancelled ||
                row.status === SepaTransactionStatusEnum.Reversed;
              const kbSelected = keyboardNav && kbSelectedId === row.id;
              const rowClickable = keyboardNav || !!onRowClick;
              return (
                <TableRow
                  key={row.id}
                  ref={(el) => {
                    if (el) rowRefs.current.set(row.id, el);
                    else rowRefs.current.delete(row.id);
                  }}
                  onClick={
                    rowClickable
                      ? () => {
                          if (keyboardNav) setKbSelectedId(row.id);
                          onRowClick?.(row);
                        }
                      : undefined
                  }
                  className={`text-left text-[15px] ${rowClickable ? "cursor-pointer" : ""} ${isVoided ? "[&_td]:text-slate-400" : ""} ${
                    kbSelected
                      ? "bg-sky-100 ring-1 ring-inset ring-sky-300 hover:bg-sky-100"
                      : selected
                        ? "bg-sky-50 hover:bg-sky-50"
                        : `${zebra} hover:bg-slate-100/70`
                  }`}
                >
                  {selectable && (
                    <TableCell className="w-10 py-1 pl-4" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selected}
                        onCheckedChange={() => onToggle?.(row.id)}
                        aria-label="Auswählen"
                      />
                    </TableCell>
                  )}
                  <TableCell
                    className={`py-1 font-medium text-slate-800 ${isVoided ? "line-through" : ""}`}
                  >
                    {row.transactionNumber}
                  </TableCell>
                  <TableCell className="py-1 text-slate-700">
                    <span className="inline-flex items-center gap-1.5">
                      <span>
                        {row.patient
                          ? `${row.patient.firstName ?? ""} ${row.patient.lastName ?? ""}`.trim() ||
                            "\u2014"
                          : "\u2014"}
                      </span>
                      {row.patient?.id != null && (
                        <Tooltip title="Zum Patienten">
                          <a
                            href={patientHref(row.patient.id)}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                              e.preventDefault();
                              navigateToPatient(
                                navigate,
                                row.patient!.id,
                                null,
                                PATIENT_SUBTABS.BILLING,
                              );
                            }}
                            className="text-slate-400 transition-colors hover:text-[#367b9a]"
                            aria-label="Zum Patienten"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        </Tooltip>
                      )}
                    </span>
                  </TableCell>
                  <TableCell className="py-1 text-slate-700">{row.rx?.rxNumber ?? "—"}</TableCell>
                  <TableCell className="whitespace-nowrap py-1 text-slate-600">
                    {fmtDate(row.rx?.date as string | null | undefined)}
                  </TableCell>
                  <TableCell className="whitespace-nowrap py-1 text-slate-600">
                    {fmtDate(row.createdAt)}
                  </TableCell>
                  <TableCell
                    className={`whitespace-nowrap py-1 text-right font-semibold ${
                      isMoneyOut ? "text-rose-600" : "text-slate-800"
                    }`}
                  >
                    {fmtEur(isMoneyOut ? Math.abs(Number(row.amount)) : Number(row.amount))}
                  </TableCell>
                  <TableCell className="py-1 text-center text-slate-600">
                    {createdBy || "—"}
                  </TableCell>
                  <TableCell className="py-1 text-center">
                    <Badge
                      variant="outline"
                      className={`text-sm font-semibold no-underline ${STATUS_CLASS[row.status] ?? ""}`}
                    >
                      {STATUS_LABEL[row.status] ?? row.status}
                    </Badge>
                  </TableCell>
                  {showText && (
                    <TableCell className="py-1 text-slate-600">
                      {row.text ? (
                        <Tooltip title={row.text}>
                          <span className="block max-w-[240px] truncate">{row.text}</span>
                        </Tooltip>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </TableCell>
                  )}
                  {showExportColumn && (
                    <TableCell className="py-1 text-center" onClick={(e) => e.stopPropagation()}>
                      {row.exportBatch ? (
                        <button
                          type="button"
                          onClick={() => onOpenBatch?.(row.exportBatch!)}
                          className="inline-flex cursor-pointer items-center rounded-full border border-solid border-sky-300 bg-sky-50 px-2 py-0.5 text-sm font-semibold text-sky-700 hover:bg-sky-100"
                          title="Zum Export wechseln"
                        >
                          Nr. {row.exportBatch.batchNumber}
                        </button>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </TableCell>
                  )}
                  <TableCell className="py-1 text-center" onClick={(e) => e.stopPropagation()}>
                    {row.document?.id ? (
                      <Tooltip title="Beleg herunterladen">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 border-0 bg-transparent text-[#367b9a] shadow-none hover:bg-transparent hover:text-[#367b9a] focus-visible:ring-0 focus-visible:ring-offset-0 [&_svg]:size-6"
                          onClick={() => onDownloadDocument(row.document!.id)}
                          aria-label="Beleg herunterladen"
                        >
                          <FileText />
                        </Button>
                      </Tooltip>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </TableCell>
                  {!hideActions && (
                    <TableCell className="py-1 text-center" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center gap-1.5">
                        <Tooltip
                          title={
                            isReversal
                              ? "Stornobuchung"
                              : canReverse
                                ? "Transaktion stornieren"
                                : row.status === SepaTransactionStatusEnum.Exported
                                  ? "In einem Export enthalten – zuerst Export zurückrollen"
                                  : row.status === SepaTransactionStatusEnum.Settled
                                    ? "Abgeschlossene Transaktion – bitte Rückerstattung erstellen"
                                    : "Bereits storniert"
                          }
                        >
                          <span className="inline-flex">
                            <Button
                              size="icon"
                              className="h-7 w-7 border-0 bg-red-600 text-white shadow-none transition-all duration-200 hover:scale-110 hover:bg-red-700 focus-visible:ring-0 focus-visible:ring-offset-0 active:scale-95 disabled:pointer-events-none disabled:opacity-40 [&_svg]:size-5"
                              onClick={() => onReverse?.(row)}
                              disabled={busy || !canReverse}
                              aria-label="Stornieren"
                            >
                              <Trash2 />
                            </Button>
                          </span>
                        </Tooltip>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default SepaTransactionTable;
